import { useEffect, useState } from "react";
import { Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import type { PermissionAction } from "@colab/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useUpdateRole } from "@/features/settings/use-update-role";
import { PermissionsField } from "./permissions-field";

type Props = {
  roleId: string;
  name: string;
  description: string | null;
  isSystem: boolean;
  initialPermissions: PermissionAction[];
  canManage: boolean;
};

export function EditRoleForm({
  roleId,
  name: initialName,
  description: initialDescription,
  isSystem,
  initialPermissions,
  canManage,
}: Props) {
  const mutation = useUpdateRole();
  const readOnly = !canManage;

  const [name, setName] = useState(initialName);
  const [description, setDescription] = useState(initialDescription ?? "");
  const [permissions, setPermissions] = useState<Set<PermissionAction>>(
    () => new Set(initialPermissions),
  );

  // Reset local state when the underlying role changes (e.g. cache refetch).
  useEffect(() => {
    setName(initialName);
    setDescription(initialDescription ?? "");
    setPermissions(new Set(initialPermissions));
  }, [initialName, initialDescription, initialPermissions]);

  function submit(e: React.FormEvent) {
    e.preventDefault();
    mutation.mutate(
      {
        id: roleId,
        patch: {
          name: name.trim(),
          description: description.trim() || null,
          permissions: Array.from(permissions),
        },
      },
      { onSuccess: () => toast.success("Role saved") },
    );
  }

  return (
    <form onSubmit={submit} className="space-y-5">
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="name" className="text-xs">
            Display name
          </Label>
          <Input
            id="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            maxLength={60}
            disabled={isSystem || readOnly}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="description" className="text-xs">
            Description
          </Label>
          <Textarea
            id="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={1}
            maxLength={2000}
            disabled={readOnly}
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label className="text-xs">Permissions</Label>
        <PermissionsField
          value={permissions}
          onChange={setPermissions}
          disabled={isSystem || readOnly}
        />
      </div>

      <div className="flex items-center justify-end gap-2 border-t pt-4">
        {readOnly && (
          <p className="mr-auto text-xs text-muted-foreground">
            You need the <code>roles.manage</code> permission to edit roles.
          </p>
        )}
        <Button type="submit" disabled={mutation.isPending || readOnly}>
          {mutation.isPending ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Save className="mr-2 h-4 w-4" />
          )}
          Save changes
        </Button>
      </div>
    </form>
  );
}
